// AWS Amplify v6 Storage imports
import { getUrl } from 'aws-amplify/storage';
import { getCurrentUser } from 'aws-amplify/auth';

export async function getDescribeTableLink(source: string): Promise<string> {
  console.log(`🎯 getDescribeTableLink called with source: ${source}`);

  // Check if user is authenticated at the AWS Amplify level
  try {
    const currentUser = await getCurrentUser();
    console.log(`✅ AWS Amplify user confirmed: ${currentUser.username}`);
  } catch (authError) {
    console.error(`❌ AWS Amplify authentication check failed:`, authError);
    console.warn('User may not be properly authenticated at AWS Amplify level');
  }
  // Based on the current IAM policy and actual file location:
  // File is at: protected/describeTable/tables.csv
  // IAM policy allows: s3:GetObject for "protected/*" resources
  const possiblePaths = [
    `protected/describeTable/${source}`, // ✅ ACTUAL LOCATION - should work with current IAM policy
    `public/describeTable/${source}`, // Alternative public location
    `protected/${source}`, // Direct protected folder
    `public/${source}`, // Direct public folder
    source, // Direct path like 'tables.csv' - likely to fail with current policy
    `data/${source}`, // Will likely fail with current policy
    `config/${source}`, // Will likely fail with current policy
    `tables/${source}`, // Will likely fail with current policy
    `metadata/${source}`, // Will likely fail with current policy
  ];

  for (const path of possiblePaths) {
    // Retry logic for placeholder URLs
    let retries = 3;
    while (retries > 0) {
      try {
        console.log(
          `Attempting to get storage URL for path: ${path} (attempt ${4 - retries}/3)`,
        );
        console.log(
          'Storage config - bucket:',
          process.env.REACT_APP_USER_FILES_S3_BUCKET,
        );
        console.log(
          'Storage config - region:',
          process.env.REACT_APP_AWS_REGION,
        );

        const linkResult = await getUrl({
          path: path,
        });
        const url = linkResult.url.toString();

        console.log(`🌐 GENERATED S3 URL: ${url}`);
        console.log(`📍 PATH USED: ${path}`);
        console.log(`🪣 BUCKET: ${process.env.REACT_APP_USER_FILES_S3_BUCKET}`);
        console.log(`🗺️ REGION: ${process.env.REACT_APP_AWS_REGION}`);

        // Check for invalid URLs that might return HTML instead of CSV
        if (url.includes('placeholder-url') || url === 'placeholder-url') {
          console.error(
            `❌ CRITICAL: Amplify Storage returned placeholder URL instead of real S3 URL!`,
          );
          console.error(
            `🔧 This means AWS Amplify Storage is not properly configured or user lacks permissions.`,
          );
          console.error(`📋 Check the following:`);
          console.error(
            `   1. Cognito Identity Pool exists and has the right S3 permissions`,
          );
          console.error(
            `   2. S3 bucket exists: ${process.env.REACT_APP_USER_FILES_S3_BUCKET}`,
          );
          console.error(
            `   3. User is properly authenticated to AWS (not just the app)`,
          );
          console.error(`   4. Amplify Storage configuration is correct`);

          retries -= 1;
          if (retries > 0) {
            console.warn(
              `🔄 Retrying placeholder URL (attempt ${4 - retries}/3)...`,
            );
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
          } else {
            console.error(
              `🚫 All retries exhausted for ${path}, trying next path`,
            );
            break;
          }
        }

        // Try to test if the URL actually returns CSV content
        try {
          console.log(`🔍 Testing URL accessibility: ${url}`);
          const response = await fetch(url);
          const contentType = response.headers.get('content-type');
          const content = await response.text();

          console.log(`📊 Response status: ${response.status}`);
          console.log(`📊 Content-Type: ${contentType}`);
          console.log(`📊 Content length: ${content.length} characters`);
          console.log(`📊 Content preview (first 300 chars):`);
          console.log(content.substring(0, 300));

          // Check if it's HTML content - be more specific
          if (
            content.trim().startsWith('<!DOCTYPE html>') ||
            content.trim().startsWith('<html')
          ) {
            console.warn(
              `❌ URL returned HTML content instead of CSV for ${path}, trying next path`,
            );
            continue;
          }

          // Check if it's an error or empty
          if (!response.ok || content.trim().length === 0) {
            console.warn(
              `❌ URL returned error or empty content for ${path}, trying next path`,
            );
            continue;
          }

          // Check if it looks like CSV
          const firstLine = content.split('\n')[0];
          if (firstLine && firstLine.includes(',')) {
            console.log(
              `✅ Content appears to be CSV format (first line: ${firstLine})`,
            );
            console.log(
              `✅ Storage link successfully validated for ${path}: ${url}`,
            );
            return url;
          } else {
            console.warn(
              `❌ Content doesn't look like CSV for ${path}. First line: ${firstLine}`,
            );
            continue;
          }
        } catch (fetchError) {
          if (
            fetchError instanceof Error &&
            fetchError.message.includes('AccessDenied')
          ) {
            console.warn(
              `S3 permissions error for ${path}: Access denied. Need s3:GetObject permission.`,
            );
          } else {
            console.warn(
              `Could not validate storage URL for ${path}:`,
              fetchError,
            );
          }
          break; // Exit retry loop and try next path
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes('AccessDenied')) {
          console.warn(
            `S3 permissions error for ${path}: Access denied. Need s3:GetObject permission.`,
          );
        } else {
          console.warn(`Could not get storage link for ${path}:`, error);
        }
        break; // Exit retry loop and try next path
      }
    } // End of retry while loop
  } // End of paths for loop

  console.warn(
    `Could not get storage link for ${source} at any of the attempted paths due to permissions or file not found`,
  );
  console.warn(
    '📋 If this is a permissions issue, the Cognito Identity Pool role needs:',
  );
  console.warn(
    '   - s3:ListBucket on arn:aws:s3:::staging-data-explorer-queries',
  );
  console.warn(
    '   - s3:GetObject on arn:aws:s3:::staging-data-explorer-queries/*',
  );
  return '';
}
